import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { InputComponent } from "@components/input/input.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { TranslateDirective } from "@ngx-translate/core";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { SelectorComponent } from "~app/common/components/selector/selector.component";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ITypeAheadDTO } from "~app/common/models/common/ITypeAheadDTO";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { CommandService } from "~app/common/services/cqs/command.service";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { DirtyTabComponent } from "~app/features/regelverk-viol/dirtyTabComponent";
import { SystemadministrationService } from "../../systemadministration.service";

export interface IVirkeskopNvb {
    ansvarigVirkeskopare: string;
    kontraktId: number;
    kontraktnummer: string;
    namn: string;
}

@Component({
    selector: "rask-sysadm-misc",
    templateUrl: "./sysadm-misc.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        BlockComponent,
        TextareaComponent,
        FormsModule,
        InputComponent,
        ButtonComponent,
        TranslateDirective,
        SelectorComponent,
        TogglePanelComponent,
        ListComponent,
        ListColumnDirective
    ]
})
export class SysadmMiscComponent extends DirtyTabComponent {
    byteConvertField = "message";
    currentCommand = null;
    ejLeveransvirkeKontraktToRensa: IVirkeskopNvb[];
    leveransvirkeKontraktToRensa: IVirkeskopNvb[];
    rawMsg = `<?xml version="1.0" encoding="utf-8"?>
    <ns0:SkotningAvslutadArbetsorder xmlns:ns0="http://Sveaskog.RASK/SkotningAvslutadArbetsorder">
        <ns0:ObjektNr isID="true">AJ8171</ns0:ObjektNr>
        <ns0:DatumTidUtfort>2024-03-27T00:10:00</ns0:DatumTidUtfort>
        <ns0:DatumTid>
            2020-07-18T00:18:47.5903792+01:00</ns0:DatumTid>
    </ns0:SkotningAvslutadArbetsorder>`;
    message = `
    {
        "$type": "SkotningAvslutadArbetsorderCommand",
        "message": ${JSON.stringify(this.rawMsg)}
    }`;

    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly modalService = inject(RaskModalService);
    private readonly systemadministrationService = inject(SystemadministrationService);
    private readonly toasterService = inject(ToastService);

    constructor() {
        const dirtyFormService = inject(DirtyFormService);

        super(dirtyFormService);
    }

    canSave() {
        // Abstrakt metod som krävs, men som inte gör något i just denna komponent
    }

    readonly getRensaEjLevvKontraktNvb = () => {
        this.busyService.addWork(this.systemadministrationService.getRensaEjLevvKontraktNvb()).subscribe(p => {
            this.leveransvirkeKontraktToRensa = p;
        });
    };

    readonly getRensaLevvKontraktNvb = () => {
        this.busyService.addWork(this.systemadministrationService.getRensaLevvKontraktNvb()).subscribe(p => {
            this.ejLeveransvirkeKontraktToRensa = p;
        });
    };

    isDirtyCheck(): boolean {
        return false;
    }

    commandSelected(typeAhead: ITypeAheadDTO) {
        if (ObjectUtils.isNullOrUndefined(typeAhead?.beskrivning)) {
            return;
        }
        this.message = typeAhead.beskrivning;
    }

    commandFormatter = (item) => item;

    commandViewFormatter = (item) => item.namn;

    commandTypeAhead = (input: string) => this.systemadministrationService.query({
        $type: "GetCommandQuery",
        query: input
    });

    readonly rensaKontraktNVB = () => {
        this.modalService.confirm(new ModalSettings("Vill du rensa alla gamla ej gällande Naturvärdesbedömningar?",
            "VARNING - DETTA GÅR EJ ATT ÅTERSTÄLLA EFTER DET GJORTS, ÄR DU HELT SÄKER PÅ ATT DU VILL RENSA?"))
            .subscribe(result => {
                if (result === ConfirmDialogResponse.save) {
                    this.busyService.addWork(this.systemadministrationService.rensaKontraktNvb()).subscribe(() => {
                        this.toasterService.add("Gamla NVB har rensats på kontrakten.", 1, 10000);
                    });
                }
            });
    };

    restoreChanges() {
        // Abstrakt metod som krävs, men som inte gör något i just denna komponent
    }

    saveChanges() {
        // Abstrakt metod som krävs, men som inte gör något i just denna komponent
    }

    sendCommand() {
        const cmd = JSON.parse(this.message);

        if (this.byteConvertField) {
            const byteFields = this.byteConvertField.split(" ").map(g => g.trim());
            byteFields.forEach(field => {
                cmd[field] = btoa(cmd[field]);
            });
        }

        this.commandService.addTransactionCommand(cmd);
        const obs = this.commandService.pushCommandQueueTransaction();

        this.busyService.addWork(obs);
        obs.subscribe(x => {
            this.commandService.reset();
        });
    }

    sendCommandService() {
        const cmd = JSON.parse(this.message);

        if (this.byteConvertField) {
            const byteFields = this.byteConvertField.split(" ").map(g => g.trim());
            byteFields.forEach(field => {
                cmd[field] = btoa(cmd[field]);
            });
        }

        const obs = this.systemadministrationService.scheduleCommand(cmd, 1);

        this.busyService.addWork(obs);
        obs.subscribe(x => {
            this.commandService.reset();
        });
    }
}
